import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import InfoTooltip from './InfoTooltip';
import './WixAuthVisual.css';

/**
 * Brand visual beside the account form in a platform onboarding (step 0).
 *
 * The carousel shows ONE real action at a time and auto-advances through every
 * capability we offer (the visitor can also step back/forward). App name + title +
 * desc reuse the SAME i18n keys as the Studio skill picker (wix_sub_* /
 * skill_flow_*), so copy is consistent and already localized in all 8 languages,
 * and every card maps to a shipped action.
 *
 * The marquee, pinned to the bottom, shows the full breadth of what the agent can
 * connect to — apps, integrations/skills, knowledge sources, channels — each logo
 * labelled with the portal's InfoTooltip on hover.
 *
 * DEFAULTS ARE THE WIX SET, so `<WixAuthVisual />` keeps behaving exactly as before.
 * Other platform onboardings (WordPress) pass their own `actions` / `tools` / copy
 * keys instead of forking the component. An action's app name may be an i18n key
 * (`app`) or a literal brand name (`appLabel`), since brands are proper nouns.
 */
const WIX_ACTIONS = [
  { logo: '/wix/bookings.png', app: 'wix_sub_bookings', title: 'skill_flow_wix_book_label', desc: 'skill_flow_wix_book_desc' },
  { logo: '/wix/bookings.png', app: 'wix_sub_bookings', title: 'skill_flow_wix_bookings_label', desc: 'skill_flow_wix_bookings_desc' },
  { logo: '/wix/stores.png', app: 'wix_sub_stores', title: 'skill_flow_wix_products_label', desc: 'skill_flow_wix_products_desc' },
  { logo: '/wix/stores.png', app: 'wix_sub_stores', title: 'skill_flow_wix_order_label', desc: 'skill_flow_wix_order_desc' },
  { logo: '/wix/restaurants.png', app: 'wix_sub_restaurants', title: 'skill_flow_wix_restaurant_menu_label', desc: 'skill_flow_wix_restaurant_menu_desc' },
  { logo: '/wix/restaurants.png', app: 'wix_sub_restaurants', title: 'skill_flow_wix_restaurant_book_label', desc: 'skill_flow_wix_restaurant_book_desc' },
  { logo: '/wix/events.png', app: 'wix_sub_events', title: 'skill_flow_wix_events_label', desc: 'skill_flow_wix_events_desc' },
  { logo: '/wix/plans.png', app: 'wix_sub_plans', title: 'skill_flow_wix_plans_label', desc: 'skill_flow_wix_plans_desc' },
  { logo: '/wix/crm.png', app: 'wix_sub_crm', title: 'skill_flow_wix_lead_label', desc: 'skill_flow_wix_lead_desc' },
];

// Everything the agent can connect to: Wix apps, integrations/skills, knowledge
// sources, channels. Brand names are proper nouns, so they stay as literals.
const TOOLS = [
  { src: '/wix/bookings.png', name: 'Wix Bookings' },
  { src: '/wix/stores.png', name: 'Wix Stores' },
  { src: '/wix/restaurants.png', name: 'Wix Restaurants' },
  { src: '/wix/events.png', name: 'Wix Events' },
  { src: '/wix/plans.png', name: 'Wix Pricing Plans' },
  { src: '/wix/crm.png', name: 'Wix CRM' },
  { src: '/stripe-logo.png', name: 'Stripe' },
  { src: '/calcom-logo.png', name: 'Cal.com' },
  { src: '/calendly-logo.png', name: 'Calendly' },
  { src: '/hubspot-logo.png', name: 'HubSpot' },
  { src: '/mailchimp-logo.png', name: 'Mailchimp' },
  { src: '/zendesk-logo.png', name: 'Zendesk' },
  { src: '/zapier_logo.png', name: 'Zapier' },
  { src: '/google-drive.png', name: 'Google Drive' },
  { src: '/onedrive.png', name: 'OneDrive' },
  { src: '/notion-logo.png', name: 'Notion' },
  { src: '/facebook-logo.webp', name: 'Facebook' },
  { src: '/instagram-logo.webp', name: 'Instagram' },
  { src: '/whatsapp-logo.webp', name: 'WhatsApp' },
];

const ROTATE_MS = 3400;

// Platform-neutral action set: shipped integrations that work on ANY site, reusing
// the Studio skill-picker keys (already localized in all 8 languages). Used by the
// WordPress onboarding, which has no platform sub-apps to show.
export const GENERIC_ACTIONS = [
  { logo: '/calendly-logo.png', appLabel: 'Calendly', title: 'skill_flow_cal_book_label', desc: 'skill_flow_cal_book_desc' },
  { logo: '/calcom-logo.png', appLabel: 'Cal.com', title: 'skill_flow_calcom_slots_label', desc: 'skill_flow_calcom_slots_desc' },
  { logo: '/hubspot-logo.png', appLabel: 'HubSpot', title: 'skill_flow_hs_capture_label', desc: 'skill_flow_hs_capture_desc' },
  { logo: '/calcom-logo.png', appLabel: 'Cal.com', title: 'skill_flow_calcom_book_label', desc: 'skill_flow_calcom_book_desc' },
  { logo: '/mailchimp-logo.png', appLabel: 'Mailchimp', title: 'skill_flow_mc_subscribe_label', desc: 'skill_flow_mc_subscribe_desc' },
  { logo: '/hubspot-logo.png', appLabel: 'HubSpot', title: 'skill_flow_hs_lookup_label', desc: 'skill_flow_hs_lookup_desc' },
];

// Same marquee minus the Wix-only logos, plus WordPress.
export const GENERIC_TOOLS = [
  { src: '/wordpress-logo.png', name: 'WordPress' },
  ...TOOLS.filter((x) => !x.src.startsWith('/wix/')),
];

export default function WixAuthVisual({
  actions = WIX_ACTIONS,
  tools = TOOLS,
  eyebrowKey = 'wav_eyebrow',
  titleKey = 'wav_title',
  subKey = 'wav_sub',
}) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = actions.length;
  const go = useCallback((d) => setIdx((i) => (i + d + n) % n), [n]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % n), ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, n]);

  const a = actions[idx];

  return (
    <div className="wav" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="wav-grid" aria-hidden="true" />
      <div className="wav-glow" aria-hidden="true" />
      <div className="wav-inner">
        <div className="wav-top">
          <span className="wav-eyebrow">{t(eyebrowKey)}</span>
          <h3 className="wav-title">{t(titleKey)}</h3>
          <p className="wav-sub">{t(subKey)}</p>

          <div className="wav-carousel">
            <div className="wav-action" key={idx}>
              <div className="wav-action-head">
                <span className="wav-action-logo"><img src={a.logo} alt="" /></span>
                <span className="wav-action-app">{a.app ? t(a.app) : a.appLabel}</span>
                <span className="wav-action-dot" aria-hidden="true" />
              </div>
              <span className="wav-action-title">{t(a.title)}</span>
              <span className="wav-action-desc">{t(a.desc)}</span>
            </div>

            <div className="wav-controls">
              <button type="button" className="wav-arrow" onClick={() => go(-1)} aria-label={t('wav_prev')}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <div className="wav-dots">
                {actions.map((act, i) => (
                  <button type="button" key={act.title} className={`wav-dot ${i === idx ? 'wav-on' : ''}`}
                    onClick={() => setIdx(i)} aria-label={String(i + 1)} />
                ))}
              </div>
              <button type="button" className="wav-arrow" onClick={() => go(1)} aria-label={t('wav_next')}>
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        <div className="wav-more">
          <span className="wav-more-label">{t('wav_more')}</span>
          <div className="wav-marquee">
            <div className="wav-marquee-track">
              {[...tools, ...tools].map((tool, i) => (
                <InfoTooltip key={i} text={tool.name} placement="top" compact>
                  <span className="wav-more-logo"><img src={tool.src} alt={tool.name} /></span>
                </InfoTooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
